// AttachmentCarousel.tsx
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getFileTypeIcon } from "../../utils/functions/file-icon-extractor";
import CircularLoader from "../../components/Loader/CircularLoader";
import { AttachmentItem } from "./AttachmentInput";
import { AttachmentPreviewItem } from "../../pages/Communications/utils/types";
import { useGetAttachmentMutation } from "../../redux/services/communicationApi";
import { Tooltip } from "../Tooltip/Tooltip";
import DownloadIcon from "../../assets/svg/download-icon";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { VF_ROLES } from "../../pages/UserManagement/utils/constants";
import MediaUnavailableLockIcon from "../../assets/svg/media-unavailable-lock-icon";
import ClipLoader from "./ClipLoader";

type Props = {
  items: AttachmentItem[];
  onPreview: (item: AttachmentItem, index?: number) => void;
  onRemove: (id: string) => void;
  hideDeleteButton?: boolean;
  isSent?: boolean;
};

function fileExt(name = "") {
  const m = name.split(".");
  return m.length > 1 ? m[m.length - 1].toLowerCase() : "";
}

function isImageMime(mime?: string) {
  return !!mime && mime.startsWith("image/");
}

const AttachmentCarousel: React.FC<Props> = ({
  items,
  onPreview,
  onRemove,
  hideDeleteButton = false,
  isSent,
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [isGettingAttachment, setIsGettingAttachment] = useState(false);
  const [getAttachment] = useGetAttachmentMutation();
  const user = useSelector((state: RootState) => state.auth.user);
  const isVfUser = VF_ROLES.includes(user?.role || "");

  const handleDownload = async (attachment: AttachmentPreviewItem) => {
    setIsGettingAttachment(true);
    try {
      const response = await getAttachment({
        s3_key: attachment.s3_key as string,
      }).unwrap();

      // Fetch the file as blob to force download
      const fetchResponse = await fetch(response);
      const blob = await fetchResponse.blob();

      // Create blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = attachment.file_name || "download";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download attachment:", error);
    } finally {
      setIsGettingAttachment(false);
    }
  };

  const checkScrollButtons = useCallback(() => {
    if (!carouselRef.current) {
      setShowLeftButton(false);
      setShowRightButton(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1; // -1 for rounding errors
    const isOverflowing = scrollWidth > clientWidth;

    setShowLeftButton(isOverflowing && canScrollLeft);
    setShowRightButton(isOverflowing && canScrollRight);
  }, []);

  // Memoize filtered image items to avoid complex expression in dependency array
  const imageItems = useMemo(
    () => items.filter((it) => isImageMime(it.mime)),
    [items],
  );

  useEffect(() => {
    checkScrollButtons();
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      checkScrollButtons();
    };

    carousel.addEventListener("scroll", handleScroll);
    const resizeObserver = new ResizeObserver(() => {
      checkScrollButtons();
    });
    resizeObserver.observe(carousel);

    return () => {
      carousel.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [imageItems, checkScrollButtons]);

  function scrollCarousel(dir: "left" | "right") {
    if (!carouselRef.current) return;
    const width = carouselRef.current.clientWidth;
    const delta = Math.max(120, Math.floor(width * 0.6));
    carouselRef.current.scrollBy({
      left: dir === "left" ? -delta : delta,
      behavior: "smooth",
    });
    setTimeout(() => checkScrollButtons(), 100);
  }

  if (items.length === 0) {
    return null;
  }
  if (isVfUser) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-row items-center gap-3.5 border border-border-gray dark:border-border-gray-dk rounded-lg px-4.5 py-3 bg-pure-white dark:bg-pure-white-dk">
          <MediaUnavailableLockIcon className="w-[36px] h-[36px] text-verba-indigo dark:text-verba-indigo-dk" />
          <div className="flex flex-col">
            <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
              Media Unavailable
            </span>
            <p className="text-sm text-dark-gray dark:text-dark-gray-dk">
              Media files can't be displayed due to security and privacy
              restrictions.
            </p>
          </div>
        </div>
        <div
          className={`flex flex-col gap-2 w-full ${isSent ? "items-end" : "items-start"}`}
        >
          {items.map((attachment) => {
            const fileIcon = getFileTypeIcon(attachment.name);

            return (
              <div
                key={attachment.id}
                className={`flex items-center gap-3 p-2 rounded-lg ${isSent ? "ml-auto" : ""}`}
              >
                <img
                  src={fileIcon}
                  alt="File type"
                  className="w-6 h-6 object-contain"
                />
                <div className="flex flex-col flex-1">
                  <span className="text-md font-medium text-heading-text dark:text-heading-text-dk break-all">
                    {attachment.name}
                  </span>
                  {attachment.size ? (
                    <span className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                      {(attachment.size / 1024).toFixed(2)} KB
                    </span>
                  ) : null}
                </div>
                {attachment.status === "uploading" && (
                  <CircularLoader className="w-5 h-5 text-verba-blue dark:text-verba-blue-dk" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const nonImageItems = items.filter((it) => !isImageMime(it.mime));

  const maxVisible = 3;
  const visibleImageItems = imageItems.slice(0, maxVisible);
  const remainingCount = imageItems.length - maxVisible + 1;

  return (
    <div className="flex flex-col gap-3">
      {/* Non-image attachments (docs, PDFs, etc.) */}
      {/* Image carousel */}
      {imageItems.length > 0 && (
        <div className="relative flex items-center gap-2 bg-transparent dark:bg-transparent">
          {showLeftButton && (
            <button
              type="button"
              onClick={() => scrollCarousel("left")}
              aria-label="Scroll left"
              className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="text-gray-600 dark:text-gray-400 text-lg">
                ‹
              </span>
            </button>
          )}
          <div
            ref={carouselRef}
            className="flex gap-2 overflow-x-auto scrollbar-hide flex-1 "
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {visibleImageItems.map((it, index) => {
              const isLastVisible = index === maxVisible - 1;
              const showOverlay = isLastVisible && remainingCount > 0;

              return (
                <div
                  key={it.id}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk relative group p-1 ${
                    it.status === "uploading" ? "cursor-wait" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    it.status !== "uploading" && onPreview(it, index)
                  }
                >
                  <div className="w-full h-full flex items-center justify-center rounded-lg overflow-hidden relative">
                    {it.previewUrl ? (
                      <img
                        src={it.previewUrl}
                        alt={it.name}
                        className={`object-cover w-full h-full rounded ${
                          it.status === "uploading" ? "opacity-50" : ""
                        }`}
                        loading="lazy"
                      />
                    ) : (
                      <img
                        src={getFileTypeIcon(it.name)}
                        alt={`${fileExt(it.name)} file icon`}
                        className={`w-10 h-10 object-contain ${
                          it.status === "uploading" ? "opacity-50" : ""
                        }`}
                        loading="lazy"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            getFileTypeIcon("");
                        }}
                      />
                    )}
                    {it.status === "uploading" && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 dark:bg-gray-800/80 rounded-lg">
                        <CircularLoader className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
                      </div>
                    )}
                    {showOverlay && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80 dark:bg-black/80 rounded-lg">
                        <span className="text-fixed-white dark:text-fixed-white-dk font-semibold text-md">
                          +{remainingCount}
                        </span>
                      </div>
                    )}
                  </div>
                  {!hideDeleteButton &&
                    it.status !== "uploading" &&
                    !showOverlay && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(it.id);
                        }}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk text-black dark:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs z-10 shadow-sm"
                        aria-label="Remove attachment"
                      >
                        ✕
                      </button>
                    )}
                </div>
              );
            })}
          </div>
          {showRightButton && (
            <button
              type="button"
              onClick={() => scrollCarousel("right")}
              aria-label="Scroll right"
              className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <span className="text-gray-600 dark:text-gray-400 text-lg">
                ›
              </span>
            </button>
          )}
        </div>
      )}{" "}
      {nonImageItems.length > 0 && (
        <div className="flex flex-col gap-2">
          {nonImageItems.map((attachment, index) => {
            const fileIcon = getFileTypeIcon(attachment.name);

            return (
              <div
                key={attachment.id}
                className="flex items-center gap-3 p-2  rounded-lg "
              >
                <img
                  src={fileIcon}
                  alt="File type"
                  className="w-6 h-6 object-contain"
                />
                <div className="flex flex-col flex-1">
                  <span
                    onClick={() =>
                      attachment.status !== "uploading" &&
                      onPreview(attachment, index)
                    }
                    className={`text-md font-medium text-heading-text dark:text-heading-text-dk break-all hover:text-verba-blue dark:hover:text-verba-blue-dk transition-colors hover:underline ${
                      attachment.status === "uploading"
                        ? "cursor-wait"
                        : "cursor-pointer"
                    }`}
                  >
                    {attachment.name}
                  </span>
                  {attachment.size ? (
                    <span className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                      {(attachment.size / 1024).toFixed(2)} KB
                    </span>
                  ) : null}
                </div>
                {attachment.status === "uploading" ? (
                  <CircularLoader className="w-5 h-5 text-verba-blue dark:text-verba-blue-dk" />
                ) : (
                  <>
                    <div className="w-px h-[15px] bg-pure-white dark:bg-pure-white-dk rounded-full"></div>
                    <div className="flex items-center gap-2">
                      {/* <Tooltip content="Preview">
                        <button
                          onClick={() => onPreview(attachment, index)}
                          className="hover:text-verba-blue dark:hover:text-verba-blue-dk transition-colors"
                          aria-label="Preview attachment"
                          disabled={isGettingAttachment}
                        >
                          <PreviewIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
                        </button>
                      </Tooltip> */}
                      <Tooltip content="Download">
                        <button
                          onClick={() =>
                            handleDownload({
                              s3_key: attachment.id,
                              s3_url: attachment.previewUrl || "",
                              file_name: attachment.name,
                              content_type: attachment.mime || "",
                              timestamp: new Date().toISOString(),
                            })
                          }
                          className="hover:text-verba-blue dark:hover:text-verba-blue-dk transition-colors"
                          aria-label="Download attachment"
                          disabled={isGettingAttachment}
                        >
                          {isGettingAttachment ? (
                            <CircularLoader className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" size={14} />
                          ) : (
                            <DownloadIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
                          )}
                        </button>
                      </Tooltip>
                      {!hideDeleteButton && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemove(attachment.id);
                          }}
                          className="w-5 h-5 rounded-full bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk text-black dark:text-white flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors text-xs"
                          aria-label="Remove attachment"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

AttachmentCarousel.displayName = "AttachmentCarousel";

export default AttachmentCarousel;
